import {
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  OutlinedInput,
} from "@mui/material";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import React, { useState, useEffect, useRef } from "react";
import { CloseOutlined } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { api } from "../../redux/api";
import Person2OutlinedIcon from '@mui/icons-material/Person2Outlined';
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";

const ThinkingDots = () => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      fontSize: "16px",
    }}
  >
    
    <span style={{ marginLeft: "6px" }} className="dot big">.</span>
    <span className="dot big">.</span>
    <span className="dot big">.</span>

    <style>
      {`
        .dot {
          animation: blink 1.4s infinite both;
        }

        .dot.big {
          font-size: 28px;
          line-height: 1;
        }

        .dot:nth-of-type(1) { animation-delay: 0s; }
        .dot:nth-of-type(2) { animation-delay: 0.2s; }
        .dot:nth-of-type(3) { animation-delay: 0.4s; }

        @keyframes blink {
          0% { opacity: 0; }
          20% { opacity: 1; }
          100% { opacity: 0; }
        }
      `}
    </style>
  </span>
);


function AiHelpChatBot(props) {
  /* -------------------- State -------------------- */
  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState(null); 
  const [isFirstMessage, setIsFirstMessage] = useState(false)
  /* -------------------- Refs -------------------- */
  const scrollRef = useRef(null);
  const didInitialScrollRef = useRef(false);

  const dispatch = useDispatch();
  const { account } = useSelector(state => state.queryParams);

  /* -------------------- Helpers -------------------- */
  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  };

  /* -------------------- Initial History Load -------------------- */
  useEffect(() => {
    const loadInitialHistory = async () => {
      try {
        setLoading(true)
        const res = await dispatch(
          api.endpoints.getAIHelpChatHistory.initiate({
            account,
            limit: 7,
            last_message_id: null
          }, { forceRefetch: true})
        ).unwrap();
        const { messages, hasMore, lastMessageId } = res?.data;
        if (!messages?.length) setIsFirstMessage(true)
        setMessages(messages || []);
        setCursor(lastMessageId);
        setHasMore(hasMore);

      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false)
      }
    };

    loadInitialHistory();
  }, [dispatch, account]);

  /* -------------------- Load Older History -------------------- */
  const loadMoreHistory = async () => {
    if (loadingHistory || !hasMore) return;

    const el = scrollRef.current;
    const prevHeight = el.scrollHeight;
    const prevTop = el.scrollTop;

    setLoadingHistory(true);

    try {
      const res = await dispatch(
        api.endpoints.getAIHelpChatHistory.initiate({
          account,
          limit: 7,
          last_message_id: cursor
        })
      ).unwrap();

      const older = res?.data?.messages || [];
      const nextCursor = res?.data?.lastMessageId;

      // stop if no progress
      if (!older.length || nextCursor === cursor) {
        setHasMore(false);
        return;
      }

      setMessages(prev => [...older, ...prev]);
      setCursor(nextCursor);
      setHasMore(res.data.hasMore);

      // preserve scroll position
      requestAnimationFrame(() => {
        el.scrollTop =
          el.scrollHeight - prevHeight + prevTop;
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingHistory(false);
    }
  };
  useEffect(() => {
    if (!didInitialScrollRef.current && messages.length) {
      didInitialScrollRef.current = true;
      scrollToBottom();
    }
  }, [messages]);

  /* -------------------- Scroll Handler -------------------- */
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;

    if (el.scrollTop === 0 && hasMore && !loadingHistory) {
      loadMoreHistory();
    }
  };

  /* -------------------- Send Message -------------------- */
  const handleSendMessage = async () => {
    if (!query.trim()) return;

    const userText = query;
    const tempId = Date.now();

    setQuery("");
    setIsFirstMessage(false)

    // show user message immediately
    setMessages(prev => [
      ...prev,
      {
        id: tempId,
        userMessage: userText,
        aiResponse: null,
        timestamp: new Date().toLocaleString()
      }
    ]);

    scrollToBottom();

    try {
      const res = await dispatch(
        api.endpoints.sendAIMessage.initiate({
          account,
          message: userText
        })
      ).unwrap();

      const aiResponse =
        res?.data?.message ||
        res?.data?.aiResponse ||
        "I couldn't understand that.";

      setMessages(prev =>
        prev.map(m =>
          m.id === tempId ? { ...m, aiResponse } : m
        )
      );

      scrollToBottom();
    } catch (e) {
      console.error(e);
      setMessages(prev =>
        prev.map(m =>
          m.id === tempId
            ? { ...m, aiResponse: "Something went wrong." }
            : m
        )
      );
    } finally {
      setLoading(false);
    }
  };

  /* -------------------- UI -------------------- */
  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", height: "4%" }}>
        <CloseOutlined sx={{ cursor: "pointer", color: "#333", width:"20px", paddingLeft: "40px" }} onClick={props.onClose} />
      </div>

      {isFirstMessage ? (
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "95%", }}>
          <div style={{ display: "flex", alignItems: "center", padding: "4px", flexDirection: "column", }}>
            <div><span className="AI_HELP" style={{ width: "52px", height: "52px" }}></span>
            </div>
            <h1 style={{ fontSize: "42px", color: "#666666" }}> BridgeGate AI </h1>
          </div>
          <p style={{ fontSize: "16px", color: "#666666" }}> Ask BridgeGate AI for your queries or select a help document to display </p>
          <form 
            style={{ display: "flex", justifyContent: "center", width: "100%"}}
            onSubmit={e => {
              e.preventDefault();
              handleSendMessage();
            }}
          >
            
            <FormControl sx={{ marginY: 3, marginX: "1", width: "100%", maxWidth: "1100px" }} variant="outlined">
              <OutlinedInput
                size="medium"
                id="outlined-adornment-ask-bridgegate-ai"
                placeholder="Ask BridgeGate AI"
                autoComplete="off"
                multiline
                maxRows={4}
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                sx={
                  { 
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e8e8e8", }, 
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#d0d0d0", }, 
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#d0d0d0", }, 
                     boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)", 
                     padding: "11.5px 12px"
                  }
                }
                endAdornment={<InputAdornment position="end">
                  <IconButton type="submit" disabled={query.length === 0}>
                    <SendOutlinedIcon sx={{ color: "#666666", cursor: "pointer", }} /> </IconButton>
                </InputAdornment>}
                aria-describedby="ask-bridgegate-ai-helper-text"
                inputProps={{ "aria-label": "Ask BridgeGate AI", }} />
            </FormControl>
          </form>
        </div>
      ) :
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "95%",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            position: "relative"
          }}
        >
          <p style={{ margin: 10 }}>BridgeGate AI</p>
          {loading && (
            <div
              style={{
                position: "absolute",
                inset: 0, 
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255,255,255,0.6)",
                zIndex: 10
              }}
            >
              <CircularProgress size={48} />
            </div>
          )}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "16px",
              display: "flex",
              flexDirection: "column",
              gap: "12px"
            }}
          >
            
            {loadingHistory && (
              <div style={{ textAlign: "center" }}>
                <span> Loading older message </span> <ThinkingDots />
              </div>
            )}

            {messages.map(msg => (
              <React.Fragment key={msg.id}>
                {/* User */}
                <div 
                  style={{ 
                    display:"flex", 
                    alignItems:"flex-end", 
                    alignSelf: "flex-end", 
                    maxWidth: "35%", 
                    gap:"8px", 
                    marginBotom: "6px" }}
                >
                <div 
                 style={{ 
                  padding: "8px 12px",
                  fontSize: "15px", 
                  borderRadius: "8px", 
                  backgroundColor: "#cbf0ff", 
                  whiteSpace: "pre-wrap", 
                  wordBreak: "break-word", 
                  boxShadow: "0 1px 2px rgba(0,0,0,0.1)" }} 
                >
                    {msg.userMessage}
                  </div>
                  <Person2OutlinedIcon 
                    sx={{ 
                      width: "32px", 
                      height: "32px", 
                      color: "#666", 
                      borderRadius: "50%", 
                      border: "1px solid #e0e0e0", 
                      padding: "4px" }}
                  />
                </div>

                {/* AI */}
                <div 
                  className="ai-message"
                  style={{ 
                    display: "flex", 
                    alignSelf: "flex-start", 
                    alignItems: "flex-end", 
                    gap: "8px",
                    width: "fit-content",
                    maxWidth: "65%", 
                    marginBottom: "12px" }}
                >  
                  <span className="AI_HELP" style={{ width: "32px", height: "32px", borderRadius: "50%", border: "1px solid #e0e0e0", display: "inline-block", flexShrink: 0 }} />
                  <div style={{ padding: "8px 12px", borderRadius: "8px", backgroundColor: "#f5f5f5", whiteSpace: "pre-wrap", wordBreak: "break-word", boxShadow: "0 1px 2px rgba(0,0,0,0.1)" }}>
                    {msg.aiResponse ? (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeSanitize]}
                        components={{
                          p: ({ children }) => (
                            <span style={{ display: "inline" }}>{children}</span>
                          ),
                          a: ({ children,...props }) => (
                            <a
                              {...props}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#1976d2" }}
                            >
                              {children}
                            </a>
                          ),
                        }}
                      >
                        {msg.aiResponse}
                      </ReactMarkdown>
                    ) : (
                      <>
                        <span>Thinking</span> <ThinkingDots />
                      </>
                    )}

                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>

          <form
            onSubmit={e => {
              e.preventDefault();
              handleSendMessage();
            }}
            style={{ padding: 14 }}
          >
            <FormControl fullWidth>
              <OutlinedInput
                multiline
                maxRows={4}
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                sx={
                  { 
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e8e8e8", }, 
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#d0d0d0", }, 
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#d0d0d0", },
                    "& .MuiOutlinedInput-input": {
                      fontSize: "15px",
                      lineHeight: "1.4",
                    },
                    padding: "11.5px 12px",
                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)", 
                  }
                }
                placeholder="Ask BridgeGate AI"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton type="submit" disabled={!query.trim()}>
                      <SendOutlinedIcon />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
          </form>
        </div>
      }
    </>
  );
}

export default AiHelpChatBot;
